import React, { useContext, useEffect, useState } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { FabricImage } from "fabric";
import { mdiDownload } from "@mdi/js";

import overviewRest from "../../../../../../rest/overviewRest";
import calcAreaUtils from "../../../../../../script/utils/calcAreaUtils";

import { Grid } from "../../../../../components/Grid";
import { Button } from "../../../../../components/Button";
import { VerticalViewDialog } from "./VerticalViewDialog";
import { EntryContext } from "../../../../../contexts/EntryContext";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

export const OverviewLayer = props => {
    const { plot, show } = props;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { calcSetting } = entry;

    const layerName = "overviewLayer";

    const [calculationId, setCalculationId] = useState();
    const [showEastWestViewDialog, setShowEastWestViewDialog] = useState(false);
    const [showSouthNordViewDialog, setShowSouthNordViewDialog] = useState(false);

    const isCalcInProgress = entry?.calcSetting?.calcAreas?.some(calcArea =>
        calcArea.calculations?.some(calculation => !calculation.resultAvailable)
    ) || false;


    useEffect(() => {
        const layer = plot.addLayer(layerName, 110);
        layer.onDeactivate(deactivatingLayer => {
            if (!["measureLayer", "downloadLayer"].includes(deactivatingLayer.name)) {
                layer.removeObjects();
            }
        });
    }, []);

    useEffect(() => {
        if (show && !isCalcInProgress) {
            plot.getLayer(layerName).activate();
            drawSlices();
        }
    }, [show, isCalcInProgress]);

    const drawSlices = () => {
        const layer = plot.getLayer(layerName);
        layer.removeObjects();
        setCalculationId();

        const [latestOverview] = calcSetting.calcAreas
            .filter(calcArea => calcArea.calculations !== undefined)
            .flatMap(calcArea => calcArea.calculations.map(calculation => ({ calcArea, calculation })))
            .filter(({ calculation }) => calculation.calculationType === "OVERVIEW")
            .filter(({ calculation }) => calculation.overviewResult)
            .sort((a, b) => (b.calculation.id ?? 0) - (a.calculation.id ?? 0));

        if (!latestOverview) {
            return;
        }

        drawSlice(layer, latestOverview.calcArea, latestOverview.calculation);
        setCalculationId(latestOverview.calculation.id);
    };

    const drawSlice = async (layer, calcArea, calculation) => {
        const imageBase64 = await overviewRest.getTopViewImage(calculation.id);

        FabricImage.fromURL(imageBase64).then(image => {
            const { left, top, scaleX, scaleY, angle } = calcAreaUtils.calculateSliceImageProperties(calcArea, plot, image);
            image.set({
                left: left,
                top: top,
                angle: angle,
                opacity: 0.4,
                scaleX: scaleX,
                scaleY: scaleY,
                evented: true,
                selectable: true,
                lockMovementX: true,
                lockMovementY: true,
                originX: "left",
                originY: "bottom",
                imageSmoothing: false,
                hoverCursor: "crosshair",
            });
            if (layer.active) {
                layer.addObject(image);
                image.setCoords();
            }
        });
    };

    return (
        <LayerStyle $show={show}>
            <Grid columns="1">
                <Button svgIcon={mdiDownload}
                    value={intl.get("enum.view.SOUTH_NORD")}
                    onClick={() => setShowSouthNordViewDialog(true)}
                    disabled={calculationId ? false : true} />
                <Button svgIcon={mdiDownload}
                    value={intl.get("enum.view.EAST_WEST")}
                    onClick={() => setShowEastWestViewDialog(true)}
                    disabled={calculationId ? false : true} />
            </Grid>
            {showSouthNordViewDialog &&
                <VerticalViewDialog
                    view="SOUTH_NORD"
                    calculationId={calculationId}
                    onClose={() => setShowSouthNordViewDialog(false)} />
            }
            {showEastWestViewDialog &&
                <VerticalViewDialog
                    view="EAST_WEST"
                    calculationId={calculationId}
                    onClose={() => setShowEastWestViewDialog(false)} />
            }
        </LayerStyle>
    );
};